import React, { useState, useEffect } from 'react';

const SupportHours: React.FC = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchClientsWithUsage(); }, [selectedMonth]);

  const fetchClientsWithUsage = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const clientsResponse = await fetch('https://api.camel.optitrac.com/api/clients', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const clientsData = await clientsResponse.json();
      const clientsWithUsage = await Promise.all(
        clientsData.map(async (client: any) => {
          try {
            const usageResponse = await fetch(
              `https://api.camel.optitrac.com/api/time-tracking/clients/${client.id}/usage/${selectedMonth}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            return { ...client, usage: await usageResponse.json() };
          } catch { return { ...client, usage: { support_hours_used: 0, outside_support_cost: 0 } }; }
        })
      );
      setClients(clientsWithUsage);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({ value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`, label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) });
    }
    return options;
  };

  const totalAllocated = clients.reduce((s, c) => s + (c.monthly_hours_allocated || 0), 0);
  const totalUsed = clients.reduce((s, c) => s + (c.usage?.support_hours_used || 0), 0);
  const totalOutside = clients.reduce((s, c) => s + (c.usage?.outside_support_cost || 0), 0);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: '#1e293b' }}>⏱️ Support Hours</h1>
        <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', background: 'white' }}>
          {generateMonthOptions().map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Allocated', value: `${totalAllocated}h`, color: '#3b82f6' },
          { label: 'Used', value: `${totalUsed.toFixed(1)}h`, color: '#10b981' },
          { label: 'Outside Cost', value: `$${totalOutside.toFixed(0)}`, color: '#f59e0b' },
          { label: 'Utilization', value: `${totalAllocated > 0 ? ((totalUsed / totalAllocated) * 100).toFixed(0) : 0}%`, color: '#8b5cf6' }
        ].map((s, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Client', 'Rate', 'Usage', 'Progress', 'Outside'].map((h, i) => (
                <th key={i} style={{ padding: '12px 16px', textAlign: i === 0 ? 'left' : i === 4 ? 'right' : 'center', fontWeight: 500, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const alloc = c.monthly_hours_allocated || 0, used = c.usage?.support_hours_used || 0;
              const pct = alloc > 0 ? (used / alloc) * 100 : 0;
              const color = pct > 90 ? '#ef4444' : pct > 75 ? '#f59e0b' : '#3b82f6';
              return (
                <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 500, color: '#1e293b' }}>{c.company_name || c.name}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b' }}>${c.hourly_rate || 0}/hr</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color }}>{used.toFixed(1)} / {alloc}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: '3px' }} />
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: (c.usage?.outside_support_cost || 0) > 0 ? '#f59e0b' : '#cbd5e1', fontWeight: 500 }}>${(c.usage?.outside_support_cost || 0).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {clients.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No clients found</div>}
      </div>
    </div>
  );
};

export default SupportHours;
